/**
 * Loss Timeline Chart
 * 
 * Shows the timeline of business interruption with:
 * - Historical performance (baseline)
 * - Loss event marker
 * - Actual vs But-For revenue
 * - Loss period shading
 * 
 * Court-ready styling with professional annotations.
 */

import React, { useRef, useEffect } from 'react';
import * as d3 from 'd3';

interface TimelineDataPoint {
  month: string;
  actual: number;
  projected?: number;
  isLossPeriod?: boolean;
}

interface LossTimelineChartProps {
  data: TimelineDataPoint[];
  lossStartDate?: string;
  lossEndDate?: string;
  title?: string;
  width?: number;
  height?: number;
  showLegend?: boolean;
}

export const LossTimelineChart: React.FC<LossTimelineChartProps> = ({
  data,
  lossStartDate,
  lossEndDate,
  title = "Business Interruption Loss Timeline",
  width = 800,
  height = 400,
  showLegend = true
}) => {
  const svgRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    if (!svgRef.current || !data.length) return;

    // Clear previous content
    d3.select(svgRef.current).selectAll("*").remove();

    const margin = { top: 60, right: 120, bottom: 60, left: 80 };
    const innerWidth = width - margin.left - margin.right;
    const innerHeight = height - margin.top - margin.bottom;

    const svg = d3.select(svgRef.current)
      .attr("width", width)
      .attr("height", height)
      .attr("viewBox", `0 0 ${width} ${height}`)
      .style("font-family", "'Inter', -apple-system, sans-serif");

    const g = svg.append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    // Scales
    const xScale = d3.scaleBand()
      .domain(data.map(d => d.month))
      .range([0, innerWidth])
      .padding(0.1);

    const maxValue = d3.max(data, d => Math.max(d.actual, d.projected || 0)) || 0;
    const yScale = d3.scaleLinear()
      .domain([0, maxValue * 1.1])
      .range([innerHeight, 0]);

    // Loss period shading
    if (lossStartDate && lossEndDate) {
      const lossData = data.filter(d => d.isLossPeriod);
      if (lossData.length > 0) {
        const startX = xScale(lossData[0].month) || 0;
        const endX = (xScale(lossData[lossData.length - 1].month) || 0) + xScale.bandwidth();
        
        g.append("rect")
          .attr("x", startX)
          .attr("y", 0)
          .attr("width", endX - startX)
          .attr("height", innerHeight)
          .attr("fill", "#FEE2E2")
          .attr("opacity", 0.5);

        // Loss period label
        g.append("text")
          .attr("x", (startX + endX) / 2)
          .attr("y", 20)
          .attr("text-anchor", "middle")
          .attr("fill", "#DC2626")
          .attr("font-size", "12px")
          .attr("font-weight", "600")
          .text("LOSS PERIOD");
      }
    }

    // Grid lines
    g.append("g")
      .attr("class", "grid")
      .attr("opacity", 0.1)
      .call(d3.axisLeft(yScale)
        .tickSize(-innerWidth)
        .tickFormat(() => "")
      );

    // Actual revenue bars
    g.selectAll(".bar-actual")
      .data(data)
      .enter()
      .append("rect")
      .attr("class", "bar-actual")
      .attr("x", d => xScale(d.month)!)
      .attr("y", d => yScale(d.actual))
      .attr("width", xScale.bandwidth() / 2)
      .attr("height", d => innerHeight - yScale(d.actual))
      .attr("fill", "#3B82F6")
      .attr("rx", 2);

    // Projected revenue bars (if available)
    const hasProjected = data.some(d => d.projected !== undefined);
    if (hasProjected) {
      g.selectAll(".bar-projected")
        .data(data.filter(d => d.projected !== undefined))
        .enter()
        .append("rect")
        .attr("class", "bar-projected")
        .attr("x", d => xScale(d.month)! + xScale.bandwidth() / 2)
        .attr("y", d => yScale(d.projected!))
        .attr("width", xScale.bandwidth() / 2)
        .attr("height", d => innerHeight - yScale(d.projected!))
        .attr("fill", "#10B981")
        .attr("rx", 2)
        .attr("opacity", 0.7);
    }

    // X Axis
    g.append("g")
      .attr("transform", `translate(0,${innerHeight})`)
      .call(d3.axisBottom(xScale))
      .selectAll("text")
      .attr("transform", "rotate(-45)")
      .attr("text-anchor", "end")
      .attr("font-size", "10px");

    // Y Axis
    g.append("g")
      .call(d3.axisLeft(yScale)
        .tickFormat(d => `$${d3.format(",.0f")(d as number)}`)
      )
      .selectAll("text")
      .attr("font-size", "10px");

    // Y Axis label
    g.append("text")
      .attr("transform", "rotate(-90)")
      .attr("y", -60)
      .attr("x", -innerHeight / 2)
      .attr("text-anchor", "middle")
      .attr("fill", "#4B5563")
      .attr("font-size", "12px")
      .text("Revenue ($)");

    // Title
    svg.append("text")
      .attr("x", width / 2)
      .attr("y", 30)
      .attr("text-anchor", "middle")
      .attr("fill", "#111827")
      .attr("font-size", "16px")
      .attr("font-weight", "600")
      .text(title);

    // Legend
    if (showLegend) {
      const legend = svg.append("g")
        .attr("transform", `translate(${width - margin.right + 20}, ${margin.top})`);

      // Actual
      legend.append("rect")
        .attr("x", 0)
        .attr("y", 0)
        .attr("width", 16)
        .attr("height", 16)
        .attr("fill", "#3B82F6")
        .attr("rx", 2);

      legend.append("text")
        .attr("x", 24)
        .attr("y", 12)
        .attr("fill", "#4B5563")
        .attr("font-size", "12px")
        .text("Actual");

      if (hasProjected) {
        legend.append("rect")
          .attr("x", 0)
          .attr("y", 24)
          .attr("width", 16)
          .attr("height", 16)
          .attr("fill", "#10B981")
          .attr("rx", 2);

        legend.append("text")
          .attr("x", 24)
          .attr("y", 36)
          .attr("fill", "#4B5563")
          .attr("font-size", "12px")
          .text("But-For");
      }

      // Loss period indicator
      legend.append("rect")
        .attr("x", 0)
        .attr("y", hasProjected ? 48 : 24)
        .attr("width", 16)
        .attr("height", 16)
        .attr("fill", "#FEE2E2")
        .attr("stroke", "#DC2626")
        .attr("stroke-width", 1)
        .attr("rx", 2);

      legend.append("text")
        .attr("x", 24)
        .attr("y", hasProjected ? 60 : 36)
        .attr("fill", "#4B5563")
        .attr("font-size", "12px")
        .text("Loss Period");
    }

  }, [data, lossStartDate, lossEndDate, title, width, height, showLegend]);

  return (
    <div className="loss-timeline-chart bg-white rounded-lg p-4 shadow-sm border">
      <svg ref={svgRef} />
    </div>
  );
};

export default LossTimelineChart;
